import { Table } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import classNames from 'classnames'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { ReportFragment, ReportStatus } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { noFilterOption, useReportStore } from '../ReportStore'

interface PyramidReportsTableProps {
  reports: ReportFragment[]
  showMoreButtonVisible: boolean
}

export const PyramidReportsTable: FC<PyramidReportsTableProps> = ({ reports, showMoreButtonVisible }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)
  const { tableActionButtonsRowWidth } = tableActionButtons()

  const [selectedCountry] = useReportStore((state) => [state.selectedCountry])

  const columns: ColumnProps<ReportFragment>[] = [
    {
      key: 'fileName',
      title: t('reports.table.filename'),
      className: 'minWidth-column-xl textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return reportA.filename.localeCompare(reportB.filename)
      },
      render: (rowData: ReportFragment) => <>{rowData.filename}</>,
    },
    {
      key: 'type',
      title: t('reports.table.type'),
      className: 'minWidth-column-l textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return reportA.type.localeCompare(reportB.type)
      },
      render: (rowData: ReportFragment) => <>{t(`reports.type.${rowData.type}`)}</>,
    },
    {
      key: 'country',
      title: t('reports.table.country'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return reportA.country.localeCompare(reportB.country)
      },
      render: (rowData: ReportFragment) => <>{rowData.country}</>,
    },
    {
      key: 'size',
      title: t('reports.table.size'),
      className: 'minWidth-column-s textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return reportA.size - reportB.size
      },
      render: (rowData: ReportFragment) => <>{rowData.size}</>,
    },
    {
      key: 'format',
      title: t('reports.table.format'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return reportA.format.localeCompare(reportB.format)
      },
      render: (rowData: ReportFragment) => <>{rowData.format}</>,
    },
    {
      key: 'date',
      title: t('reports.table.date'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return new Date(reportA.date).getTime() - new Date(reportB.date).getTime()
      },
      render: (rowData: ReportFragment) => <>{formatDateShort(rowData.date.toString())}</>,
    },
    {
      key: 'actions',
      title: t('reports.table.actions'),
      className: 'textAlign-left',
      width: tableActionButtonsRowWidth(1),
      render: (rowData: ReportFragment) => (
        <PerTableActionButtons
          variant="pastel"
          buttons={[
            {
              icon: 'download_fill',
              color: 'primary',
              asLink: rowData.status === ReportStatus.Processed ? rowData.downloadLink : undefined,
              tooltip: rowData.status === ReportStatus.Processed ? t('reports.table.downloadTooltip') : t('reports.table.errorTooltip'),
              disabled: rowData.status !== ReportStatus.Processed,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <Table
        className={classNames('light small scroll-compensation', {
          viewWithTabs: !showMoreButtonVisible,
          viewWithTabsAndShowMoreButton: showMoreButtonVisible,
        })}
        dataSource={reports.map((data) => ({ ...data, key: data.id }))}
        columns={columns}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: (
            <PerEmpty
              icon="noReport_stroke"
              heading={
                !selectedCountry || selectedCountry.value === noFilterOption.value
                  ? t('reports.table.empty')
                  : t('reports.table.filteredEmpty')
              }
            />
          ),
        }}
      />
    </>
  )
}
